import CoreBase from '../../../services/engines/CoreBase';
import journalService from '../../../services/JournalService';
import responseHelper from '../../../services/ResponseHelper';

const _name = 'WorkspaceBeSync';
const WS_FORMAT = 'workspace_data';
const WS_SERVICE = 'Jerny.Workspaces';

/**
 * Writes workspace snapshots to the BE as Jerny notes via journal/create (upsert:
 * no rk = create, rk = update). 1 push per workspace in flight, a save during a push
 * is queued and only the latest snapshot is sent afterwards (no double creates).
 */
class WorkspaceBeSync extends CoreBase {
    _name = _name;
    _inflight = new Set(); // workspace rk's with a running push
    _queued = new Map(); // workspace rk → { snapshot, onBeRk }

    /**
     * @param {object} snapshot - { rk, name, cards, beRk? }
     * @param {function} onBeRk - called with the BE rk after a create
     */
    push = async (snapshot, onBeRk) => {
        if (this._inflight.has(snapshot.rk)) {
            this._queued.set(snapshot.rk, { snapshot, onBeRk });
            return;
        }

        this._inflight.add(snapshot.rk);
        try {
            const beRk = await this._post(snapshot, 0);
            if (beRk && !snapshot.beRk) onBeRk?.(beRk);
            this._runQueued(snapshot.rk, beRk);
        } finally {
            this._inflight.delete(snapshot.rk);
        }
    };

    /**
     * Soft delete (stateId 999), no-op when the workspace never reached the BE.
     * @param {object} snapshot
     */
    remove = async (snapshot) => {
        if (!snapshot?.beRk) return;
        await this._post(snapshot, 999);
    };

    // ─── private ──────────────────────────────────────────

    _runQueued = (rk, beRk) => {
        const next = this._queued.get(rk);
        if (!next) return;
        this._queued.delete(rk);
        // the queued snapshot was built before the create returned, so it may miss beRk
        setTimeout(() => this.push({ ...next.snapshot, beRk: next.snapshot.beRk ?? beRk }, next.onBeRk), 0);
    };

    /**
     * @returns {Promise<string|null>} BE rk, null on failure
     */
    _post = async (snapshot, stateId) => {
        try {
            const { beRk, ...content } = snapshot;
            const data = {
                rk: beRk ?? null,
                title: `Workspace ${snapshot.name}`,
                body: JSON.stringify(content),
                format: WS_FORMAT,
                service: WS_SERVICE,
                type: 'Config',
                cat: 'Workspace',
                stage: 'Done',
                stateId,
            };
            const resp = await journalService.createOrUpdate({ data });
            if (responseHelper.check(resp, '', `${_name}._post`) < 0) {
                this.logError('_post', `failed for ${snapshot.rk}`);
                return null;
            }
            return resp.data?.rk ?? resp.data?.RK ?? beRk ?? null;
        } catch (error) {
            this.logError('_post', error.message);
            return null;
        }
    };
}

const workspaceBeSync = new WorkspaceBeSync();
export default workspaceBeSync;
